import { localGroupById } from 'services/db/Queries/GroupQueries'
import {
  AccountDoc,
  EventDoc,
  GroupDoc,
  GroupParticipantType,
  UserSettingsDoc
} from 'types/api/changes'

export enum TierFeatures {
  PRIORITY_SUPPORT = 'prioritySupport',
  LANDLINE = 'landline',
  ADMINS = 'numAdmins',
  BUSINESSINFO = 'business',
  PERMISSIONS = 'permManagement',
  PERMISSIONS_EVENTS = 'permManagementAddEvents',
  PERMISSIONS_CALENDAR_NAME = 'permManagementCalendarName',
  PERMISSIONS_MEMBERS = 'permManagementAddMembers',
  VERIFIED = 'verifiedChannels',
  CHANNEL = 'calendarChannels',
  MEMBERCOLOR = 'memberColors',
  RSVP = 'rsvp',
  SYNC_WITH_CALENDARS = 'syncEvents',
  ASSIGN_EVENT_OWNERSHIP = 'assignEventOwnership',
  EMBED_CALENDARS = 'embedCalendars',
  EDIT_THEIR_EVENTS_ONLY = 'membersCanEditTheirEventsOnly',
  REMOVE_BRANDING = 'removeBranding',
  SHARE_PERSONAL_CALENDAR = 'sharePersonalCalendar',
  SEE_EVENTS_DETAILS = 'seeEventsDetails'
}

import { isGroupArchived, isGroupHidden } from './userSettings'
import { Tier } from './accounts'

import { GroupPrivacy } from 'model/models/Group'
import { is24meApp } from 'utils/appType'
import {
  icloudPrefix,
  outlookPrefix,
  prefixGoogleEventsGroup
} from 'utils/constants'
import LocalStorage from 'services/LocalStorage'
import { LocalStorageKey } from 'types/services/localStorage'
import { isTrulyEmpty } from 'utils/object'

export function isGroupInSupplementaryGroups(
  group: string,
  event?: EventDoc
): boolean {
  return (event?.SupplementaryGroupsIDs ?? []).includes(group)
}

export async function provideReferenceGroup(
  event: EventDoc | undefined,
  userSettings: UserSettingsDoc | undefined,
  currentGroup?: string | null,
  userId?: string | null
): Promise<string | undefined> {
  if (
    currentGroup &&
    currentGroup.length > 0 &&
    (currentGroup === event?.GroupID ||
      isGroupInSupplementaryGroups(currentGroup, event))
  ) {
    return currentGroup
  }

  if (isConvertedGroupId(event?.GroupID)) {
    return event?.GroupID
  }

  const eventGroup = await localGroupById(event?.GroupID ?? '')

  if (
    !isGroupHidden(userSettings, event?.GroupID ?? '') &&
    isUserParticipantOfAGroup(eventGroup ?? '', userId ?? '')
  ) {
    return event?.GroupID
  }

  return event?.SupplementaryGroupsIDs?.find(
    (group) =>
      !isGroupHidden(userSettings, group) &&
      !isGroupArchived(userSettings, group)
  )
}

export function isUserParticipantOfAGroup(
  group: GroupDoc | undefined | null,
  userID: string
): boolean {
  if (!group) return false

  const participant = group?.Participants?.[userID]
  return participant != undefined && participant != null
}

export function isOutOfTierGroup(
  currentTier: Tier,
  group?: GroupDoc,
  accountDoc?: AccountDoc,
  userId?: string
) {
  if (!group) return false
  if (group.OwnerID != userId) return false
  if (isConvertedGroup(group) && isTrulyEmpty(group.PrivateLinkUrl))
    return false

  return outOfTierFeatures(currentTier, group, accountDoc, userId).length > 0
}

export function isUserCanOperateEvents(group?: GroupDoc, userId?: string) {
  if (is24meApp()) return true

  if (!group || !userId) return false

  if (
    isConvertedGroup(group) &&
    group.OwnerID === LocalStorage.get(LocalStorageKey.USER_ID)
  )
    return true

  const isAdmin =
    group.Participants?.[userId]?.Type === GroupParticipantType.ADMIN
  const canAddAll = group.GroupSettings?.IsAllParticipantsCanAddItems === '1'

  return isAdmin || canAddAll
}

export function canUserDoFeature(
  feature: TierFeatures,
  currentTier: Tier,
  group?: GroupDoc,
  userId?: string,
  accountDoc?: AccountDoc
) {
  if (group && group.OwnerID != userId) return true // for all old groups or for non-owners, we mark all features as allowed

  switch (feature) {
    case TierFeatures.RSVP:
      if (currentTier < minTierRequired(TierFeatures.RSVP)) return false
      break
    case TierFeatures.PRIORITY_SUPPORT:
      if (currentTier < minTierRequired(TierFeatures.PRIORITY_SUPPORT))
        return false
      break

    case TierFeatures.LANDLINE:
      if (group) {
        if (group.OwnerID === userId) {
          if (
            accountDoc &&
            currentTier < minTierRequired(TierFeatures.LANDLINE) &&
            accountDoc.PhoneNumberType === '2'
          )
            return false
        }
      } else {
        if (
          currentTier < minTierRequired(TierFeatures.LANDLINE) &&
          accountDoc &&
          accountDoc.PhoneNumberType === '2'
        )
          return false
      }
      break

    case TierFeatures.ADMINS:
      if (Number(group?.Version ?? '1') < 2) return true
      if (group) {
        if (amountOfAdminsInGroup(group) > allowedAdminsForTier(currentTier)) {
          return false
        } else {
          return true
        }
      }
      break

    case TierFeatures.BUSINESSINFO:
      if (group) {
        if (
          group.BusinessInfo?.Active === '1' &&
          currentTier < minTierRequired(TierFeatures.BUSINESSINFO)
        )
          return false
        else return true
      }

      if (currentTier < minTierRequired(TierFeatures.BUSINESSINFO)) return false
      break
    case TierFeatures.PERMISSIONS:
      if (
        currentTier < minTierRequired(TierFeatures.PERMISSIONS) &&
        group &&
        !isAllPermissionsEnabled(group)
      )
        return false
      break
    case TierFeatures.PERMISSIONS_CALENDAR_NAME:
      if (
        group?.GroupSettings &&
        currentTier < minTierRequired(TierFeatures.PERMISSIONS_CALENDAR_NAME) &&
        group &&
        group.GroupSettings.IsAllParticipantsCanEditGroupMetadata != '1'
      )
        return false
      break
    case TierFeatures.PERMISSIONS_EVENTS:
      if (
        group?.GroupSettings &&
        currentTier < minTierRequired(TierFeatures.PERMISSIONS_EVENTS) &&
        group &&
        group.GroupSettings.IsAllParticipantsCanAddItems != '1'
      )
        return false
      break
    case TierFeatures.PERMISSIONS_MEMBERS:
      if (
        group?.GroupSettings &&
        currentTier < minTierRequired(TierFeatures.PERMISSIONS_MEMBERS) &&
        group &&
        group.GroupSettings.IsAllParticipantsCanAddParticipants != '1'
      )
        return false
      break
    case TierFeatures.VERIFIED:
      if (group) {
        if (
          currentTier < minTierRequired(TierFeatures.VERIFIED) &&
          group.VerifiedGroupStatus === '2'
        ) {
          return false
        } else {
          return true
        }
      }
      if (currentTier < minTierRequired(TierFeatures.VERIFIED)) return false
      break

    case TierFeatures.CHANNEL:
      if (group) {
        if (
          group.PrivacyMode === GroupPrivacy.CHANNEL &&
          currentTier < minTierRequired(TierFeatures.CHANNEL)
        ) {
          return false
        } else {
          return true
        }
      }
      if (currentTier < Tier.BUSINESS_PLUS) return false
      break
    case TierFeatures.MEMBERCOLOR:
      if (group) {
        if (
          currentTier < minTierRequired(TierFeatures.MEMBERCOLOR) &&
          groupHasMembersWithColor(group)
        ) {
          return false
        } else {
          return true
        }
      }
      if (currentTier < minTierRequired(TierFeatures.MEMBERCOLOR)) return false
      break
    case TierFeatures.SYNC_WITH_CALENDARS:
      if (currentTier < minTierRequired(TierFeatures.SYNC_WITH_CALENDARS))
        return false
      break
    case TierFeatures.ASSIGN_EVENT_OWNERSHIP:
      if (
        currentTier < minTierRequired(TierFeatures.ASSIGN_EVENT_OWNERSHIP) &&
        group &&
        group?.GroupSettings?.IsAllParticipantsCanEditOnlyTheirEvents === '1'
      )
        return false
      else return true

    default:
      return true
  }

  return true
}

export function minTierRequired(feature: TierFeatures, amount?: number) {
  switch (feature) {
    case TierFeatures.PRIORITY_SUPPORT:
    case TierFeatures.MEMBERCOLOR:
    case TierFeatures.PERMISSIONS:
    case TierFeatures.PERMISSIONS_CALENDAR_NAME:
    case TierFeatures.PERMISSIONS_EVENTS:
    case TierFeatures.PERMISSIONS_MEMBERS:
    case TierFeatures.RSVP:
      return Tier.PRO

    case TierFeatures.BUSINESSINFO:
    case TierFeatures.LANDLINE:
    case TierFeatures.ASSIGN_EVENT_OWNERSHIP:
    case TierFeatures.EDIT_THEIR_EVENTS_ONLY:
      return Tier.BUSINESS

    case TierFeatures.CHANNEL:
    case TierFeatures.VERIFIED:
    case TierFeatures.SYNC_WITH_CALENDARS:
    case TierFeatures.EMBED_CALENDARS:
      return Tier.BUSINESS_PLUS

    case TierFeatures.ADMINS:
      if ((amount ?? 0) >= 3) {
        return Tier.BUSINESS_PLUS
      } else if ((amount ?? 0) >= 1) {
        return Tier.BUSINESS
      } else {
        return Tier.FREE
      }

    default:
      // Handle default case if necessary
      return Tier.FREE
  }
}

export function outOfTierFeatures(
  tier: Tier,
  group?: GroupDoc,
  account?: AccountDoc,
  user?: string
): TierFeatures[] {
  const outOfTierFeatures: TierFeatures[] = []
  if (!canUserDoFeature(TierFeatures.ADMINS, tier, group, user, account))
    outOfTierFeatures.push(TierFeatures.ADMINS)
  if (!canUserDoFeature(TierFeatures.BUSINESSINFO, tier, group, user, account))
    outOfTierFeatures.push(TierFeatures.BUSINESSINFO)
  if (!canUserDoFeature(TierFeatures.CHANNEL, tier, group, user, account))
    outOfTierFeatures.push(TierFeatures.CHANNEL)
  if (!canUserDoFeature(TierFeatures.LANDLINE, tier, group, user, account))
    outOfTierFeatures.push(TierFeatures.LANDLINE)
  if (!canUserDoFeature(TierFeatures.MEMBERCOLOR, tier, group, user, account))
    outOfTierFeatures.push(TierFeatures.MEMBERCOLOR)
  if (
    !canUserDoFeature(
      TierFeatures.PERMISSIONS_CALENDAR_NAME,
      tier,
      group,
      user,
      account
    )
  )
    outOfTierFeatures.push(TierFeatures.PERMISSIONS_CALENDAR_NAME)
  if (
    !canUserDoFeature(
      TierFeatures.PERMISSIONS_EVENTS,
      tier,
      group,
      user,
      account
    )
  )
    outOfTierFeatures.push(TierFeatures.PERMISSIONS_EVENTS)
  if (
    !canUserDoFeature(
      TierFeatures.PERMISSIONS_MEMBERS,
      tier,
      group,
      user,
      account
    )
  )
    outOfTierFeatures.push(TierFeatures.PERMISSIONS_MEMBERS)
  if (!canUserDoFeature(TierFeatures.VERIFIED, tier, group, user, account))
    outOfTierFeatures.push(TierFeatures.VERIFIED)
  if (
    !canUserDoFeature(
      TierFeatures.ASSIGN_EVENT_OWNERSHIP,
      tier,
      group,
      user,
      account
    )
  )
    outOfTierFeatures.push(TierFeatures.ASSIGN_EVENT_OWNERSHIP)

  return outOfTierFeatures
}

export function groupHasMembersWithColor(group: GroupDoc) {
  if (group.Participants) {
    const participants = Object.values(group.Participants)

    if (participants.length > 0) {
      return participants.some(
        (participant) =>
          participant.MemberColor &&
          participant.MemberColor.length > 0 &&
          participant.MemberColor != 'null'
      )
    }
  }

  return false
}

export function isAllPermissionsEnabled(group: GroupDoc) {
  return (
    group.GroupSettings.IsAllParticipantsCanAddItems === '1' &&
    group.GroupSettings.IsAllParticipantsCanAddParticipants === '1' &&
    group.GroupSettings.IsAllParticipantsCanEditGroupMetadata === '1'
  )
}

export function amountOfAdminsInGroup(group: GroupDoc) {
  let admins = 0

  if (group.Participants) {
    const participants = Object.values(group.Participants)

    if (participants.length > 0) {
      participants.forEach((participant) => {
        if (participant.Type === GroupParticipantType.ADMIN) admins += 1
      })
    }
  }

  return admins
}

function allowedAdminsForTier(tier: Tier) {
  switch (tier) {
    case Tier.PRO:
      return 1
    case Tier.BUSINESS:
      return 3
    case Tier.BUSINESS_PLUS:
      return 999
    default:
      return 1
  }
}

/**
 *
 * @param group to check if it is a converted group
 * @returns true if id contains prefix of a converted group
 */
export function isConvertedGroup(group: GroupDoc | undefined) {
  if (!group) return false
  return (
    isConvertedGroupId(group._id?.toLowerCase() ?? '') ||
    isConvertedGroupId(group.CalendarEmail?.toLowerCase() ?? '')
  )
}

export function isConvertedGroupId(groupId?: string) {
  if (!groupId) return false
  return [
    prefixGoogleEventsGroup.toLowerCase(),
    outlookPrefix.toLowerCase(),
    icloudPrefix.toLowerCase()
  ].some((prefix) => groupId?.toLowerCase().includes(prefix))
}
